import React, { useEffect, useRef } from 'react';

declare global {
  interface Window {
    mousePosX: any;
    mousePosY: any;
  }
}

const MagneticField = ({ isVisible }: MagneticFieldProps) => {
  const fpsInterval = 1000 / 20;
  let then: any;
  let elapsed;
  let now;
  let stop = true;

  const width = 800;
  const height = 480;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const u = 32;
  const highCount = height / u;
  const wideCount = width / u;
  const count = Math.floor(highCount * wideCount);
  const mods: {
    draw2: (canvas: HTMLCanvasElement) => void;
    update: (canvas: HTMLCanvasElement) => void;
  }[] = [];

  function Module(this: any, _x: any, _y: any) {
    this.x = _x;
    this.y = _y;
    this.a = 0;
  }

  Module.prototype.update = function (canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mouseX = window.mousePosX || 0;
    const mouseY = window.mousePosY || 0;
    const x = mouseX - rect.left - this.x;
    const y = mouseY - rect.top - this.y;
    this.a = Math.atan2(y, x);
  };

  Module.prototype.draw2 = function (canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    const isOrange = !!canvas.closest('*[data-anim="orange"]');

    if (context) {
      context.save();
      context.beginPath();
      context.translate(this.x, this.y);
      context.rotate(this.a);
      context.moveTo(0, 0);
      context.lineTo(10, 0);
      context.lineWidth = 2;
      context.strokeStyle = isOrange
        ? 'rgba(255, 154, 0, 0.15)'
        : 'rgba(22 ,53, 86, 0.8)';
      context.stroke();
      context.restore();
    }
  };

  let index = 0;
  for (let xc = 0; xc < wideCount; xc++) {
    for (let yc = 0; yc < highCount; yc++) {
      mods[index++] = new (Module as any)(
        Math.floor(xc) * u,
        Math.floor(yc) * u,
      );
    }
  }

  const draw = (canvas: HTMLCanvasElement) => {
    const context = canvas.getContext('2d');

    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.save();
      context.translate(20, 20);

      for (let i = 0; i < count; i++) {
        mods[i].update(canvas);
        mods[i].draw2(canvas);
      }

      context.restore();
    }
  };

  const render = (canvas: any, newtime: any) => {
    if (stop) {
      return;
    }

    // request another frame
    requestAnimationFrame(() => render(canvas, window.performance.now()));

    // calc elapsed time since last loop
    now = newtime;
    elapsed = now - then;

    if (elapsed > fpsInterval) {
      // Get ready for next frame by setting then=now, but...
      // Also, adjust for fpsInterval not being multiple of 16.67
      then = now - (elapsed % fpsInterval);
      draw(canvas);
    }
  };

  useEffect(() => {
    if (canvasRef.current) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      then = window.performance.now();

      const observerOptions = {
        rootMargin: '0px',
        threshold: 0.1,
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            stop = false;
            render(canvasRef.current, window.performance.now());
          } else {
            stop = true;
          }
        });
      }, observerOptions);

      observer.observe(canvasRef.current);
    }
  }, [canvasRef.current, isVisible]);

  if (isVisible) {
    return <canvas ref={canvasRef} width={width} height={height}></canvas>;
  } else {
    return <></>;
  }
};

export type MagneticFieldProps = {
  isVisible: boolean;
};

export default MagneticField;
